import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ScrollTopButton from '@/components/feature/ScrollTopButton';
import SEO from '@/components/feature/SEO';
import { mangaList, categories } from '@/mocks/manga';
import type { MangaItem } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [filteredManga, setFilteredManga] = useState<MangaItem[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredManga(mangaList);
    } else {
      setFilteredManga(mangaList.filter((m) => m.category === activeCategory));
    }
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryName = currentCategory ? currentCategory.name : t('category.all');
  const categoryDescription = currentCategory ? currentCategory.description : '';

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${categoryName} - ${t('category.title')} | ${t('site.name')}`}
        description={`本子网站${categoryName}分类，${categoryDescription || '探索海量优质漫画作品'}。正版高清漫画在线阅读，日系漫画、同人志、原创本子一网打尽。`}
        canonical={`https://www.benziwz.com/category/${activeCategory}`}
      />
      <main>
        <Navbar />
        <div className="pt-20 pb-14 md:pb-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto">
            <Breadcrumb
              items={[
                { label: t('category.title'), path: '/category/all' },
                { label: categoryName },
              ]}
            />

            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mt-4 mb-2">
              {categoryName}漫画
            </h1>
            {categoryDescription && (
              <p className="text-sm text-foreground-500 mb-8">{categoryDescription}</p>
            )}

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer border ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 border-primary-500 text-background-50'
                      : 'bg-transparent border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-600'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredManga.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredManga.map((manga) => (
                  <MangaCard key={manga.id} manga={manga} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">
                  {t('category.noResults')}
                </p>
              </div>
            )}
          </div>
        </div>
        <Footer />
        <ScrollTopButton />
      </main>
    </>
  );
}